import React, { useEffect } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MapContainer, GeoJSON, useMap } from 'react-leaflet';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { buildGeoespacialInventory } from '../../utils/reportCalculations/geoespacialInventory';

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatArea = (value) => {
  const n = toNumberOrNull(value);
  if (n === null) return '-';
  return n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value) => {
  const n = toNumberOrNull(value);
  if (n === null) return '-';
  return `${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`;
};

const DEFAULT_CENTER = [-15.8, -47.9];
const DEFAULT_ZOOM = 5;

// Configuração padrão de ícones do Leaflet (mesmo padrão usado no mapa principal)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
  iconUrl: new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href,
  shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
});

function FitBoundsOnFeatures({ features }) {
  const map = useMap();

  useEffect(() => {
    if (!features || !Array.isArray(features) || features.length === 0) return;

    try {
      const layer = L.geoJSON({ type: 'FeatureCollection', features });
      const bounds = layer.getBounds();
      if (bounds && bounds.isValid && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [4, 4] });
      }
    } catch (_e) {
      // falha de ajuste de bounds não deve quebrar o relatório
    }
  }, [features, map]);

  return null;
}

function MiniLayerMap({ areaFeatures, layerFeatures, disabled = false }) {
  const areaArr = Array.isArray(areaFeatures) ? areaFeatures : [];
  const layerArr = Array.isArray(layerFeatures) ? layerFeatures : [];
  const allFeatures = [...areaArr, ...layerArr];
  const hasAnyFeature = allFeatures.length > 0;

  if (!hasAnyFeature) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 1,
          bgcolor: 'grey.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Sem geometria
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: disabled ? 'grey.100' : 'white',
      }}
    >
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        minZoom={3}
        maxZoom={19}
        attributionControl={false}
        zoomControl={false}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        boxZoom={false}
        keyboard={false}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: disabled ? '#f5f5f5' : 'white',
          pointerEvents: 'none',
        }}
      >
        {areaArr.length > 0 && (
          <GeoJSON
            data={{ type: 'FeatureCollection', features: areaArr }}
            style={() => ({
              color: disabled ? '#b0bec5' : '#f9a825',
              weight: 2,
              fillColor: disabled ? '#eceff1' : '#fff9c4',
              fillOpacity: disabled ? 0.3 : 0.35,
            })}
          />
        )}

        {layerArr.length > 0 && (
          <GeoJSON
            data={{ type: 'FeatureCollection', features: layerArr }}
            style={() => ({
              color: disabled ? '#90a4ae' : '#0d47a1',
              weight: 2,
              fillColor: disabled ? '#cfd8dc' : '#42a5f5',
              fillOpacity: disabled ? 0.4 : 0.5,
            })}
          />
        )}

        <FitBoundsOnFeatures features={allFeatures} />
      </MapContainer>
    </Box>
  );
}

const ENV_CARD_ORDER = [
  'ÁREA DO IMÓVEL',
  'APP',
  'RESERVA LEGAL',
  'USO RESTRITO',
  'ÁREA CONSOLIDADA',
  'VEGETAÇÃO NATIVA',
  'ÁREA NÃO CLASSIFICADA',
  'ÁREA DE SERVIDÃO',
  'ÁREA DE POUSIO',
];

const ENV_CARD_LABELS = {
  'ÁREA DO IMÓVEL': 'Área do Imóvel',
  APP: 'APP',
  'RESERVA LEGAL': 'Reserva Legal',
  'USO RESTRITO': 'Uso Restrito',
  'ÁREA CONSOLIDADA': 'Área Consolidada',
  'VEGETAÇÃO NATIVA': 'Vegetação Nativa',
  'ÁREA NÃO CLASSIFICADA': 'Área Não Classificada',
  'ÁREA DE SERVIDÃO': 'Área de Servidão Administrativa',
  'ÁREA DE POUSIO': 'Área de Pousio',
};

function _normalizeAliasForId(alias) {
  if (!alias) return '';
  return String(alias)
    .normalize('NFD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .toLowerCase();
}

function _MapScreenshotCapture() {
  // No-op: captura será feita em utilitário dedicado off-screen.
  return null;
}

export default function GeoespacialTab({ snapshot }) {
  const { _summaryAreas, carItems, sigefItems } = buildGeoespacialInventory(snapshot);

  const hasAnyCar = Array.isArray(carItems) && carItems.length > 0;
  const hasAnySigef = Array.isArray(sigefItems) && sigefItems.length > 0;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Geoespacial - Inventário de Áreas Registradas
      </Typography>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">CAR - Área do Imóvel por código</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {!hasAnyCar && (
            <Typography variant="body2" color="text.secondary">
              Nenhum registro de CAR encontrado para este relatório.
            </Typography>
          )}

          {hasAnyCar && (
            <Box>
              {carItems.map((item, index) => (
                <Accordion key={item.codigo || index} defaultExpanded={index === 0} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Código CAR: {item.codigo || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Área do Imóvel declarada (CAR): {formatArea(item.areaImovelDeclaradaHa)} ha
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        mt: 2,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                        gap: 2,
                      }}
                    >
                      {ENV_CARD_ORDER.map((alias) => {
                        const layer =
                          alias === 'ÁREA DO IMÓVEL'
                            ? item.areaImovelLayer
                            : item.layers && item.layers[alias];

                        const declaredHa =
                          layer && layer.declaredHa != null
                            ? layer.declaredHa
                            : alias === 'ÁREA DO IMÓVEL'
                              ? item.areaImovelDeclaradaHa
                              : null;
                        const calculatedHa =
                          layer && layer.calculatedHa != null ? layer.calculatedHa : null;
                        const deltaHa = layer && layer.deltaHa != null ? layer.deltaHa : null;
                        const deltaPct = layer && layer.deltaPct != null ? layer.deltaPct : null;

                        const areaFeatures = item.areaImovelFeatures || [];
                        const layerFeatures =
                          alias === 'ÁREA DO IMÓVEL'
                            ? item.areaImovelFeatures || []
                            : layer && Array.isArray(layer.features)
                              ? layer.features
                              : [];

                        const isEnvTheme = alias !== 'ÁREA DO IMÓVEL';
                        const declaredNum = toNumberOrNull(declaredHa);
                        const calculatedNum = toNumberOrNull(calculatedHa);
                        const isDeclaredZeroOrEmpty = declaredNum === null || declaredNum === 0;
                        const isCalculatedZeroOrEmpty =
                          calculatedNum === null || calculatedNum === 0;
                        const isDisabled =
                          isEnvTheme && isDeclaredZeroOrEmpty && isCalculatedZeroOrEmpty;

                        return (
                          <Box
                            key={alias}
                            sx={{
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              p: 1.5,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.75,
                              bgcolor: isDisabled ? 'grey.100' : 'background.paper',
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {ENV_CARD_LABELS[alias] || alias}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'stretch',
                                gap: 1.5,
                                minHeight: 160,
                              }}
                            >
                              <Box
                                sx={{
                                  flex: '0 0 50%',
                                  height: '100%',
                                }}
                              >
                                <MiniLayerMap
                                  areaFeatures={areaFeatures}
                                  layerFeatures={layerFeatures}
                                  disabled={isDisabled}
                                />
                              </Box>
                              <Box
                                sx={{
                                  flex: '0 0 50%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  Área Cadastrada (ha): {formatArea(declaredHa)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Área Calculada (ha): {formatArea(calculatedHa)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Δ área (ha): {formatArea(deltaHa)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Δ área (%): {formatPercent(deltaPct)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">SIGEF - Parcelas e cobertura sobre o imóvel</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {!hasAnySigef && (
            <Typography variant="body2" color="text.secondary">
              Nenhum registro SIGEF encontrado para este relatório.
            </Typography>
          )}

          {hasAnySigef && (
            <Box>
              {sigefItems.map((item, index) => (
                <Accordion key={item.codigo || index} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Código SIGEF: {item.codigo || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Área total SIGEF (ha): {formatArea(item.areaTotalSigefHa)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Parcelas SIGEF vinculadas: {item.parcelsCount}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {(!Array.isArray(item.parcels) || item.parcels.length === 0) && (
                      <Typography variant="body2" color="text.secondary">
                        Nenhuma parcela SIGEF com geometria processada.
                      </Typography>
                    )}

                    {Array.isArray(item.parcels) && item.parcels.length > 0 && (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                          gap: 2,
                          mt: 1,
                        }}
                      >
                        {item.parcels.map((parcel) => {
                          const baseFeatures = Array.isArray(item.baseFeatures)
                            ? item.baseFeatures
                            : [];
                          const parcelFeatures = Array.isArray(parcel.features)
                            ? parcel.features
                            : [];
                          return (
                            <Box
                              key={parcel.parcela}
                              sx={{
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 1.5,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.75,
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Parcela {parcel.parcela}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'stretch',
                                  gap: 1.5,
                                  minHeight: 160,
                                }}
                              >
                                <Box
                                  sx={{
                                    flex: '0 0 50%',
                                    height: '100%',
                                  }}
                                >
                                  <MiniLayerMap
                                    areaFeatures={baseFeatures}
                                    layerFeatures={parcelFeatures}
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    flex: '0 0 50%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary">
                                    Área total SIGEF (ha): {formatArea(item.areaTotalSigefHa)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Área da parcela (ha): {formatArea(parcel.areaHa)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
